import React from 'react'

import { FastTooltip } from '../_shared/FastTooltip'
import { formatChoiceShare } from './format'

import { ShareDonutSlice, PlotRenderMode } from '@digital-twins/slide-schemas/generated/plot-types'
import { PlotDataOf, StoredPlotPayload } from '../contract'

/**
 * The share-donut plot (plots plan J): the parts of one single-select whole,
 * at most six slices with "Other" included. The alternative view of
 * choice-bars; Python stamps every share, the rollup and the colour slots, and
 * the donut draws them in payload order (no re-sort, no renormalising: a
 * slice's arc is its unrounded `exactShare` of 100, so the slices close the
 * ring however the printed shares round).
 */

export type ShareDonutPlotData = StoredPlotPayload<'share-donut'>['data'] | PlotDataOf<'share-donut'>

export interface ShareDonutPlotProps {
  data: ShareDonutPlotData
  renderMode?: PlotRenderMode
  locale?: string | null
  legend?: boolean
  tooltip?: 'share' | 'share_count'
  testIdPrefix?: string
}

const SIZE = 180
const STROKE = 34
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
/** "Other" and any unslotted slice are neutral (Rule defaults, Colours). */
const NEUTRAL = 'var(--ds-gray-500, #a0aec0)'
const TRACK = 'hsl(var(--chart-bar-track, 0 0% 90%))'

function sliceColor(slice: ShareDonutSlice): string {
  if (slice.role === 'other' || slice.colorSlot === null) {
    return NEUTRAL
  }
  return `var(--slide-chart-${slice.colorSlot})`
}

/**
 * The donut's hover list of an Other slice: the members that hold a share.
 * Python lists every folded option, zero picks included (decision 23); a
 * hover list only names what the slice is made of.
 */
function donutMembersTitle(members: Array<{ label: string; share: number }> | undefined, format: (share: number) => string): string | undefined {
  const held = (members ?? []).filter((member) => member.share > 0)
  return held.length ? held.map((member) => `${member.label}: ${format(member.share)}`).join('\n') : undefined
}

export function ShareDonutPlot(props: ShareDonutPlotProps) {
  const { data, renderMode = 'interactive', locale, legend = true, tooltip = 'share_count', testIdPrefix = 'share-donut' } = props

  const slices = data.slices ?? []
  const format = (share: number) => formatChoiceShare(share, data.decimals, locale)

  if (slices.length === 0) {
    return (
      <div className="flex items-center justify-center text-muted-foreground min-h-48" data-testid={`${testIdPrefix}-empty`}>
        {data.labels.plot_no_data ?? null}
      </div>
    )
  }

  let offset = 0
  const arcs = slices.map((slice) => {
    // legacy reader only: a donut stored before `exactShare` draws the printed share.
    const length = (Math.max(0, Math.min(100, slice.exactShare ?? slice.share)) / 100) * CIRCUMFERENCE
    const arc = { slice, length, offset }
    offset += length
    return arc
  })

  return (
    <div className="flex w-full flex-col items-center gap-4" data-testid={testIdPrefix}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={slices.map((slice) => `${slice.label} ${format(slice.share)}`).join(', ')}>
        <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" stroke={TRACK} strokeWidth={STROKE} />
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          {arcs.map(({ slice, length, offset: start }, index) => (
            <circle
              key={slice.id}
              data-testid={`${testIdPrefix}-slice-${index}`}
              data-role={slice.role}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={sliceColor(slice)}
              strokeWidth={STROKE}
              strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
              strokeDashoffset={-start}
            >
              {renderMode !== 'static' ? (
                <title>
                  {tooltip === 'share_count' && typeof slice.count === 'number'
                    ? `${slice.label}: ${format(slice.share)} (${slice.count})`
                    : `${slice.label}: ${format(slice.share)}`}
                </title>
              ) : null}
            </circle>
          ))}
        </g>
      </svg>
      {legend ? (
        <ul className="w-full space-y-2" data-testid={`${testIdPrefix}-legend`}>
          {slices.map((slice, index) => (
            <li key={slice.id} className="flex items-center justify-between gap-4" data-testid={`${testIdPrefix}-legend-${index}`}>
              <span className="flex items-center gap-2 min-w-0">
                <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: sliceColor(slice), flexShrink: 0 }} />
                <FastTooltip label={renderMode === 'static' ? undefined : donutMembersTitle(slice.otherMembers, format)}>
                  <span
                    className="text-[14px] text-foreground"
                    title={renderMode === 'static' ? donutMembersTitle(slice.otherMembers, format) : undefined}
                  >
                    {slice.label}
                  </span>
                </FastTooltip>
              </span>
              <span className="text-sm font-semibold tabular-nums shrink-0">{format(slice.share)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
